import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import * as productService from '../../../service/product.service';
import {
    Product,
    ProductRequest,
    ProductSummary,
} from '../../../service/model/store.model';
import { NotificationType } from '../../../shared/enums';
import { showNotification } from '../../alerts/alert.slice';
import { getAxiosErrorMessage } from '../../../service/axios/axios.call.service';

export interface ProductState {
    products: Product[];
    summary?: ProductSummary;
    isLoading: boolean;
}

export const initialState: ProductState = {
    products: [],
    isLoading: false,
};

export const fetchProductSummary = createAsyncThunk<ProductSummary>(
    'products/summary',
    async (_, { rejectWithValue }) => {
        return productService.getProductSummary().catch((errors) => {
            return rejectWithValue(errors);
        });
    }
);

export const fetchProducts = createAsyncThunk<Product[]>(
    'products/all',
    async (_, { rejectWithValue }) => {
        return productService.getProducts().catch((errors) => {
            return rejectWithValue(errors);
        });
    }
);

export const createProduct = createAsyncThunk<Product, ProductRequest>(
    'products/create',
    async (request, { rejectWithValue, dispatch }) => {
        return productService
            .createProduct(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Product created successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchProductSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const updateProduct = createAsyncThunk<Product, ProductRequest>(
    'products/update',
    async (request, { rejectWithValue, dispatch }) => {
        return productService
            .updateProduct(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Product updated successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchProductSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const deleteProduct = createAsyncThunk<number, number>(
    'products/delete',
    async (request, { rejectWithValue, dispatch }) => {
        return productService
            .deleteProduct(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Product deleted successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchProductSummary());
                dispatch(fetchProducts());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

const productSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductSummary.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchProductSummary.fulfilled,
                (state, action: PayloadAction<ProductSummary>) => {
                    state.summary = action.payload;
                    state.isLoading = false;
                }
            )
            .addCase(fetchProductSummary.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchProducts.pending, (state) => {})
            .addCase(
                fetchProducts.fulfilled,
                (state, action: PayloadAction<Product[]>) => {
                    state.products = action.payload;
                }
            )
            .addCase(fetchProducts.rejected, (state) => {})
            .addCase(createProduct.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createProduct.fulfilled,
                (state, action: PayloadAction<Product>) => {
                    state.isLoading = false;
                }
            )
            .addCase(createProduct.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateProduct.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateProduct.fulfilled,
                (state, action: PayloadAction<Product>) => {
                    state.isLoading = false;
                }
            )
            .addCase(updateProduct.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteProduct.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteProduct.fulfilled,
                (state, action: PayloadAction<number>) => {
                    state.isLoading = false;
                }
            )
            .addCase(deleteProduct.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const selectProducts = (state: RootState) => state.products.products;
export const selectProductSummary = (state: RootState) => state.products.summary;
export const selectIsLoading = (state: RootState) => state.products.isLoading;

// export const {} = productSlice.actions;

export default productSlice.reducer;
